import React, { useEffect, useState, useMemo } from 'react';
import './ConversionsPage.css';
import DateRangePicker from './DateRangePicker';
import {
  getPresetRange,
  getConversionsByRange,
  getCampaignCanonicalMap,
  getConversionCampaigns,
  fmtMoney,
  deleteConversion,
  updateConversion,
} from '../data/dataService';

/* ── Icons ── */
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0114.13-3.88L23 10M1 14l5.36 5.36A9 9 0 0020.49 15" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function formatSalePeriod(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return text;

  let seconds = Math.floor(Number(text));
  if (!Number.isFinite(seconds) || seconds <= 0) return '';

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  return [
    days ? `${days}д.` : '',
    hours ? `${hours}ч.` : '',
    minutes ? `${minutes}м.` : '',
    seconds ? `${seconds}с.` : '',
  ].filter(Boolean).join(' ');
}

function ConversionsPage() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);
  const [range, setRange] = useState(() => ({
    ...getPresetRange('7days'),
    label: 'Последние 7 дней',
  }));

  const [campaignFilter, setCampaignFilter] = useState('');
  const [subidSearch,    setSubidSearch]    = useState('');
  const [campaignOpen,   setCampaignOpen]   = useState(false);
  const [showLeads,      setShowLeads]      = useState(true);
  const [showSales,      setShowSales]      = useState(true);
  const [editConversion, setEditConversion] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editState, setEditState] = useState('idle');
  const [editError, setEditError] = useState('');
  const [, setRevision] = useState(0);

  const handleApply = (r) => setRange(r);
  const handleReset = () =>
    setRange({ ...getPresetRange('7days'), label: 'Последние 7 дней' });

  const allConversions = getConversionsByRange(range.from, range.to, now);

  const openEditor = (conversion) => {
    setEditConversion(conversion);
    setEditForm({
      campaign: conversion.campaign || '', offer: conversion.offer || '',
      conversionTime: conversion.conversionTime || '', period: conversion.period || '',
      status: conversion.status || '', origStatus: conversion.origStatus || 'lead',
      revenue: Number(conversion.revenue) || 0, sub1: conversion.sub1 || '',
      sub2: conversion.sub2 || '', sub3: conversion.sub3 || '', sub4: conversion.sub4 || '',
      deviceModel: conversion.deviceModel || '', city: conversion.city || '',
    });
    setEditError('');
    setEditState('idle');
  };

  const closeEditor = () => { if (!['saving', 'deleting'].includes(editState)) setEditConversion(null); };
  const changeEditField = (field, value) => setEditForm((current) => ({ ...current, [field]: value }));
  const saveEditor = async (event) => {
    event.preventDefault();
    setEditState('saving');
    setEditError('');
    try {
      await updateConversion(editConversion.id, { ...editForm, revenue: Number(editForm.revenue) || 0 });
      setRevision((value) => value + 1);
      setEditState('idle');
      setEditConversion(null);
    } catch (error) {
      setEditState('error');
      setEditError(error.message || 'Не удалось сохранить конверсию');
    }
  };
  const removeEditedConversion = async () => {
    if (!window.confirm('Удалить эту конверсию из базы?')) return;
    setEditState('deleting');
    setEditError('');
    try {
      await deleteConversion(editConversion.id);
      setRevision((value) => value + 1);
      setEditState('idle');
      setEditConversion(null);
    } catch (error) {
      setEditState('error');
      setEditError(error.message || 'Не удалось удалить конверсию');
    }
  };

  const campaignCanonicalMap = useMemo(() => getCampaignCanonicalMap(), []);
  const campaigns = useMemo(() => getConversionCampaigns(), []);

  const filtered = useMemo(() => {
    let rows = allConversions;
    if (campaignFilter) {
      rows = rows.filter((c) => {
        const campaign = String(c.campaign || '').trim();
        return (campaignCanonicalMap.get(campaign) || campaign) === campaignFilter;
      });
    }
    if (subidSearch.trim()) {
      const q = subidSearch.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.sub1 || '').toLowerCase().includes(q) ||
          (c.sub2 || '').toLowerCase().includes(q) ||
          (c.sub3 || '').toLowerCase().includes(q) ||
          (c.sub4 || '').toLowerCase().includes(q),
      );
    }
    const showAllStatuses = !showLeads && !showSales;
    rows = rows.filter((c) => (
      showAllStatuses ||
      (c.origStatus === 'lead' && showLeads) ||
      (c.origStatus === 'sale' && showSales)
    ));
    // Log should show newest conversions first.
    return [...rows].sort((a, b) => {
      const ta = a.conversionTime || '';
      const tb = b.conversionTime || '';

      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return tb.localeCompare(ta);
    });
  }, [allConversions, campaignCanonicalMap, campaignFilter, showLeads, showSales, subidSearch]);

  const salesTotal = useMemo(
    () => filtered
      .filter((c) => c.origStatus === 'sale')
      .reduce((s, c) => s + (Number(c.revenue) || 0), 0),
    [filtered],
  );

  return (
    <div className="cp">
      {/* ── Page header ── */}
      <div className="cp__header">
        <h2 className="cp__title">Лог конверсий</h2>

        <div className="cp__header-right">
          <DateRangePicker value={range} onApply={handleApply} onReset={handleReset} />
          <button className="cp__icon-btn" aria-label="Обновить" onClick={() => setRange({ ...range })}>
            <RefreshIcon />
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="cp__filters">
        {/* Campaign dropdown */}
        <div className="cp__dropdown-wrap">
          <button
            className="cp__dropdown-btn"
            onClick={() => setCampaignOpen((v) => !v)}
          >
            <span>{campaignFilter ? campaignFilter.slice(0, 28) + '…' : 'Кампании'}</span>
            <ChevronIcon />
          </button>
          {campaignOpen && (
            <div className="cp__dropdown-menu">
              <div
                className={`cp__dropdown-item ${!campaignFilter ? 'cp__dropdown-item--active' : ''}`}
                onClick={() => { setCampaignFilter(''); setCampaignOpen(false); }}
              >
                Все кампании
              </div>
              {campaigns.map((c) => (
                <div
                  key={c}
                  className={`cp__dropdown-item ${campaignFilter === c ? 'cp__dropdown-item--active' : ''}`}
                  onClick={() => { setCampaignFilter(c); setCampaignOpen(false); }}
                >
                  {c}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sub ID search */}
        <input
          className="cp__search"
          type="text"
          placeholder="Искать subid"
          value={subidSearch}
          onChange={(e) => setSubidSearch(e.target.value)}
        />

        <div className="cp__status-filters" aria-label="Фильтр по статусу">
          <button
            type="button"
            aria-pressed={showLeads}
            className={`cp__status-filter cp__status-filter--lead ${showLeads ? 'cp__status-filter--active' : ''}`}
            onClick={() => setShowLeads((value) => !value)}
          >
            Лиды
          </button>
          <button
            type="button"
            aria-pressed={showSales}
            className={`cp__status-filter cp__status-filter--sale ${showSales ? 'cp__status-filter--active' : ''}`}
            onClick={() => setShowSales((value) => !value)}
          >
            Продажи
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="cp__table-card">
        <div className="cp__table-scroll">
          <table className="cp__table">
            <thead>
              <tr>
                <th>ID конверсии</th>
                <th>Кампания</th>
                <th>Оффер</th>
                <th>Время конверсии</th>
                <th>Период пр</th>
                <th>Статус</th>
                <th>Ориг. статус</th>
                <th>Доход</th>
                <th>Sub ID 1</th>
                <th>Sub ID 2</th>
                <th>Sub ID 3</th>
                <th>Sub ID 4</th>
                <th>Модель устройства</th>
                <th>Город</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={14} className="cp__empty">
                    Нет данных за выбранный период
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="cp__id-cell" title={c.id}><button type="button" className="cp__id-link" onClick={() => openEditor(c)}>{c.id.slice(0, 18)}…</button></td>
                    <td className="cp__campaign-cell" title={c.campaign}>
                      {campaignCanonicalMap.get(String(c.campaign || '').trim()) || c.campaign}
                    </td>
                    <td className="cp__offer-cell"    title={c.offer}>{c.offer}</td>
                    <td className="cp__time-cell">{c.conversionTime}</td>
                    <td>{formatSalePeriod(c.period)}</td>
                    <td>
                      <span className={`cp__badge ${c.origStatus === 'sale' ? 'cp__badge--green' : 'cp__badge--blue'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="cp__orig-status">{c.origStatus}</td>
                    <td className={c.revenue > 0 ? 'cp__positive' : 'cp__muted'}>
                      {fmtMoney(c.revenue)}
                    </td>
                    <td>{c.sub1 || ''}</td>
                    <td>{c.sub2 || ''}</td>
                    <td>{c.sub3 || ''}</td>
                    <td>{c.sub4 || ''}</td>
                    <td>{c.deviceModel || ''}</td>
                    <td>{c.city || ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="cp__table-footer">
          <span>Показано: {filtered.length} из {allConversions.length} конверсий</span>
          <span className="cp__footer-sales">Сумма продаж: <strong>{fmtMoney(salesTotal)}</strong></span>
        </div>
      </div>
      {editConversion && editForm && (
        <div className="cp__edit-overlay" onMouseDown={closeEditor}>
          <form className="cp__edit-modal" onSubmit={saveEditor} onMouseDown={(event) => event.stopPropagation()}>
            <div className="cp__edit-head"><div><h3>Редактирование конверсии</h3><span>{editConversion.id}</span></div><button type="button" onClick={closeEditor}>×</button></div>
            <div className="cp__edit-grid">
              <label className="cp__edit-wide"><span>Кампания</span><input value={editForm.campaign} onChange={(e) => changeEditField('campaign', e.target.value)} /></label>
              <label className="cp__edit-wide"><span>Оффер</span><input value={editForm.offer} onChange={(e) => changeEditField('offer', e.target.value)} /></label>
              <label><span>Время конверсии</span><input required value={editForm.conversionTime} onChange={(e) => changeEditField('conversionTime', e.target.value)} /></label>
              <label><span>Период продажи</span><input value={editForm.period} onChange={(e) => changeEditField('period', e.target.value)} /></label>
              <label><span>Статус</span><input value={editForm.status} onChange={(e) => changeEditField('status', e.target.value)} /></label>
              <label><span>Оригинальный статус</span><select value={editForm.origStatus} onChange={(e) => changeEditField('origStatus', e.target.value)}><option value="lead">lead</option><option value="sale">sale</option></select></label>
              <label><span>Доход</span><input type="number" min="0" step="0.01" value={editForm.revenue} onChange={(e) => changeEditField('revenue', e.target.value)} /></label>
              <label><span>Город</span><input value={editForm.city} onChange={(e) => changeEditField('city', e.target.value)} /></label>
              {['sub1', 'sub2', 'sub3', 'sub4'].map((field, index) => <label key={field}><span>Sub ID {index + 1}</span><input value={editForm[field]} onChange={(e) => changeEditField(field, e.target.value)} /></label>)}
              <label className="cp__edit-wide"><span>Модель устройства</span><input value={editForm.deviceModel} onChange={(e) => changeEditField('deviceModel', e.target.value)} /></label>
            </div>
            {editError && <div className="cp__edit-error">{editError}</div>}
            <div className="cp__edit-actions">
              <button className="cp__edit-delete" type="button" onClick={removeEditedConversion} disabled={editState === 'saving' || editState === 'deleting'}>{editState === 'deleting' ? 'Удаление…' : 'Удалить конверсию'}</button>
              <span className="cp__edit-actions-spacer" />
              <button type="button" onClick={closeEditor}>Отмена</button>
              <button type="submit" disabled={editState === 'saving' || editState === 'deleting'}>{editState === 'saving' ? 'Сохранение…' : 'Сохранить в базу'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default ConversionsPage;
